import {useState, useEffect} from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    Typography,
} from "@mui/material";
import ConfirmDialog from "../../components/common/confirm/ConfirmDialog";
import {useModuleEditorApi} from "./api";
import type {ModuleDescriptor, NamespaceOption} from "./types";

type CopyModuleDialogProps = {
    open: boolean;
    onClose: () => void;
    module: ModuleDescriptor;
};

export function CopyModuleDialog({open, onClose, module}: CopyModuleDialogProps) {
    const api = useModuleEditorApi();
    const [namespaces, setNamespaces] = useState<NamespaceOption[]>([]);
    const [targetNamespace, setTargetNamespace] = useState("");
    const [loading, setLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [conflictName, setConflictName] = useState("");
    const [snackbar, setSnackbar] = useState<{ message: string; severity: "success" | "error" } | null>(null);

    useEffect(() => {
        if (open) {
            setTargetNamespace("");
            api.listNamespaces().then(setNamespaces).catch(() => setNamespaces([]));
        }
    }, [open]);

    async function handleCopy(overwrite: boolean) {
        setLoading(true);
        try {
            var result = await api.copyModule(module.module_id, module.namespace, targetNamespace, overwrite);
            if (result.status === "conflict") {
                setConflictName(result.existing_name ?? module.module_id);
                setConfirmOpen(true);
                return;
            }
            setSnackbar({message: "Module copied successfully", severity: "success"});
            onClose();
        } catch (e: any) {
            setSnackbar({message: e.message || "Copy failed", severity: "error"});
        } finally {
            setLoading(false);
        }
    }

    function handleConfirmClose(confirmed: boolean) {
        setConfirmOpen(false);
        if (confirmed) {
            handleCopy(true);
        }
    }

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>Copy Module</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{mb: 2}}>
                        Copying <strong>{module.name}</strong> ({module.namespace}/{module.module_id})
                    </Typography>
                    <FormControl fullWidth variant="standard">
                        <InputLabel>Target Namespace</InputLabel>
                        <Select
                            value={targetNamespace}
                            onChange={(e) => setTargetNamespace(e.target.value)}
                            label="Target Namespace"
                        >
                            {namespaces.map((ns) => (
                                <MenuItem key={ns.key} value={ns.key}>
                                    {ns.name} ({ns.key})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        onClick={() => handleCopy(false)}
                        variant="contained"
                        disabled={!targetNamespace || loading}
                    >
                        Copy
                    </Button>
                </DialogActions>
            </Dialog>
            <ConfirmDialog
                open={confirmOpen}
                close={handleConfirmClose}
                title="Module Already Exists"
                message={`A module "${conflictName}" with this ID already exists in the target namespace. Overwrite it?`}
            />
            <Snackbar
                open={snackbar !== null}
                autoHideDuration={3000}
                onClose={() => setSnackbar(null)}
                message={snackbar?.message}
            />
        </>
    );
}
